// Brazilian phone mask utilities
import { normalizePhone } from '@/lib/sonax/normalizePhone';

export type PhoneType = 'mobile' | 'landline' | 'invalid';

/**
 * Removes all non-numeric characters from phone number
 */
export function cleanPhone(phone: string): string {
  return phone.replace(/\D/g, '');
}

/**
 * Detecta queries que são essencialmente um telefone (dígitos + formatação).
 * Usado nas buscas: o telefone é gravado só com dígitos (trigger
 * `normalize_phone_leads`), então o termo cru com máscara — "(15) 99772-5616" —
 * nunca casa num ILIKE e precisa ser reduzido a dígitos antes de filtrar.
 */
export function looksLikePhone(raw: string): boolean {
  const digits = cleanPhone(raw);
  if (digits.length < 4) return false;
  return raw.replace(/[\d\s()+\-.]/g, '').length === 0;
}

/**
 * Detects if phone is mobile (9 digits) or landline (8 digits)
 */
export function detectPhoneType(cleanPhone: string): PhoneType {
  if (cleanPhone.length === 11 && cleanPhone[2] === '9') {
    return 'mobile';
  }
  if (cleanPhone.length === 10) {
    return 'landline';
  }
  return 'invalid';
}

/**
 * Validates Brazilian phone number
 */
export function validateBrazilianPhone(phone: string): boolean {
  const clean = cleanPhone(phone);
  
  // Must have 10 or 11 digits
  if (clean.length !== 10 && clean.length !== 11) {
    return false;
  }
  
  // Must start with valid area code (11-99)
  const areaCode = parseInt(clean.substring(0, 2));
  if (areaCode < 11 || areaCode > 99) {
    return false;
  }
  
  // If 11 digits, third digit must be 9 (mobile)
  if (clean.length === 11 && clean[2] !== '9') {
    return false;
  }
  
  return true;
}

/**
 * Applies Brazilian phone mask
 */
export function applyPhoneMask(phone: string): string {
  const clean = cleanPhone(phone);
  
  if (clean.length <= 2) {
    return clean;
  }
  
  if (clean.length <= 6) {
    return `(${clean.substring(0, 2)}) ${clean.substring(2)}`;
  }
  
  if (clean.length <= 10) {
    return `(${clean.substring(0, 2)}) ${clean.substring(2, 6)}-${clean.substring(6)}`;
  }
  
  // Mobile format: (11) 99999-9999
  return `(${clean.substring(0, 2)}) ${clean.substring(2, 7)}-${clean.substring(7, 11)}`;
}

/**
 * Gets appropriate placeholder based on detected type
 */
export function getPhonePlaceholder(phone: string): string {
  const clean = cleanPhone(phone);
  const type = detectPhoneType(clean);
  
  switch (type) {
    case 'mobile':
      return '(11) 99999-9999';
    case 'landline':
      return '(11) 3333-4444';
    default:
      return '(11) 99999-9999';
  }
}

/**
 * Normaliza o telefone para armazenamento: dígitos, com DDI 55, no MESMO formato que
 * `public.normalize_phone()` grava — 12 dígitos (fixo) ou 13 (móvel, com o 9º dígito).
 *
 * Delega para o espelho canônico em vez de reimplementar. A versão anterior prefixava o 55
 * mas NÃO inseria o 9º dígito, então para móvel em formato antigo devolvia 12 dígitos onde o
 * banco tem 13: `6183330962` virava `556183330962` em vez de `5561983330962`. Como o
 * `useLookupByPhone` monta as variantes de busca da ligação entrante com esta função, um
 * retorno nesse formato simplesmente não encontrava o lead.
 *
 * Mantém `''` (e não `null`) para quem já depende do retorno string.
 */
export function normalizePhoneForStorage(phone: string): string {
  return normalizePhone(phone) ?? '';
}

/**
 * Formats phone for display
 * Handles numbers with country code (55) and + prefix by removing them before formatting
 */
export function formatPhoneForDisplay(phone: string): string {
  if (!phone) return '';
  
  // Remove + se existir antes de processar
  const phoneWithoutPlus = phone.startsWith('+') ? phone.substring(1) : phone;
  const clean = cleanPhone(phoneWithoutPlus);
  
  // Se o número começa com 55 (código do país) e tem 12 ou 13 dígitos, remover o 55
  let phoneToFormat = clean;
  if (clean.length === 12 || clean.length === 13) {
    if (clean.startsWith('55')) {
      phoneToFormat = clean.substring(2);
    }
  }
  
  // Se ainda tiver mais de 11 dígitos, usar apenas os últimos 11
  if (phoneToFormat.length > 11) {
    phoneToFormat = phoneToFormat.substring(phoneToFormat.length - 11);
  }
  
  return applyPhoneMask(phoneToFormat);
}

/**
 * Alias for formatPhoneForDisplay for compatibility
 */
function formatPhoneNumber(phone: string): string {
  return formatPhoneForDisplay(phone);
}

/**
 * Handles phone input change with mask application
 */
export function handlePhoneInputChange(
  value: string,
  currentCursorPosition: number
): { maskedValue: string; newCursorPosition: number } {
  const clean = cleanPhone(value);
  const maskedValue = applyPhoneMask(clean);
  
  // Calculate new cursor position
  let newCursorPosition = currentCursorPosition;
  
  // If we added characters (mask), adjust cursor
  if (maskedValue.length > value.length) {
    // Find how many non-digit characters were added before cursor
    let nonDigitsBefore = 0;
    for (let i = 0; i < Math.min(currentCursorPosition, maskedValue.length); i++) {
      if (!/\d/.test(maskedValue[i])) {
        nonDigitsBefore++;
      }
    }
    newCursorPosition = currentCursorPosition + nonDigitsBefore;
  }
  
  return { maskedValue, newCursorPosition };
}

/**
 * Compares two phone numbers for compatibility
 * Normalizes both numbers (removes +, spaces, etc.) and compares
 * Handles cases with/without country code (55) and with/without +
 * Returns true if the numbers represent the same phone number
 */
export function arePhonesCompatible(phone1: string | null | undefined, phone2: string | null | undefined): boolean {
  // Se ambos são null/undefined, são compatíveis (sem telefone)
  if (!phone1 && !phone2) return true;
  
  // Se apenas um tem telefone, não são compatíveis
  if (!phone1 || !phone2) return false;
  
  try {
    // Normalizar ambos os números (remove caracteres não numéricos, adiciona 55 se necessário)
    // normalizePhoneForStorage retorna sem o + (ex: 554488062820)
    const normalized1 = normalizePhoneForStorage(phone1);
    const normalized2 = normalizePhoneForStorage(phone2);
    
    // Se algum resultado for vazio, não são compatíveis
    if (!normalized1 || !normalized2) return false;
    
    // Comparar os números normalizados (ambos devem ter 55 no início)
    return normalized1 === normalized2;
  } catch (error) {
    // Se houver erro na normalização, não são compatíveis
    console.warn("Erro ao comparar telefones:", error);
    return false;
  }
}

/**
 * Validates phone number BEFORE normalization
 * This should be used before normalizePhoneForStorage to ensure the input is valid
 * Accepts phone numbers with or without mask, but expects Brazilian format (10 or 11 digits)
 */
export function validatePhoneBeforeNormalization(phone: string | null | undefined): boolean {
  if (!phone) return false;
  
  // Remove apenas caracteres não numéricos (não adiciona código do país ainda)
  const clean = cleanPhone(phone);
  
  // Deve ter 10 ou 11 dígitos (formato brasileiro sem código do país)
  if (clean.length !== 10 && clean.length !== 11) {
    return false;
  }
  
  // Deve começar com código de área válido (11-99)
  const areaCode = parseInt(clean.substring(0, 2));
  if (areaCode < 11 || areaCode > 99) {
    return false;
  }
  
  // Se tem 11 dígitos, o terceiro dígito deve ser 9 (celular)
  if (clean.length === 11 && clean[2] !== '9') {
    return false;
  }
  
  return true;
}

/**
 * Validates and normalizes phone for storage
 * First validates the phone, then normalizes it
 * Returns null if invalid, normalized phone if valid
 * Accepts both unnormalized (10-11 digits) and already normalized (with +55 or 55) phones
 * Returns format: 55 + DDD + number (e.g., 554488062820) - without +
 */
export function validateAndNormalizePhone(phone: string | null | undefined): string | null {
  if (!phone) return null;

  const clean = cleanPhone(phone);
  if (!clean) return null;

  // Valida sempre sobre o número SEM DDI, venha ele com 55 ou não.
  const local = (clean.length === 12 || clean.length === 13) && clean.startsWith('55')
    ? clean.substring(2)
    : clean;

  if (!validatePhoneBeforeNormalization(local)) return null;

  // Passou na validação: devolve a forma canônica. Antes, um número que já chegava com o 55
  // era devolvido COMO VEIO — então `556183330962` (móvel antigo, 12 dígitos) escapava sem
  // ganhar o 9º dígito e divergia do que o banco grava.
  return normalizePhone(phone);
}